import { Icon20LockOutline } from '@vkontakte/icons';
import { Button, Text, Title } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { abSharingLinkFakedoor } from 'reactApp/appHelpers/featuresHelpers';
import { isThemedSharedModalItem } from 'reactApp/appHelpers/featuresHelpers/features/themedPublic';
import { unPublishHelper } from 'reactApp/appHelpers/publishHelper';
import { renderUnpublishWeblinkDialog } from 'reactApp/components/SharingWindow/blocks/Weblink/SharingWeblink.helpers';
import { SharingNewWeblinkList } from 'reactApp/components/SharingWindow/blocks/Weblink/WeblinkList/SharingNewWeblinkList';
import { SHARING_VARIANT } from 'reactApp/components/SharingWindow/blocks/Weblink/WeblinkList/SharingNewWeblinkList.types';
import { sendGa } from 'reactApp/components/SharingWindow/Sharing.helpers';
import { EFrom } from 'reactApp/components/SharingWindow/Sharing.types';
import { contactsSlice } from 'reactApp/modules/contacts/contacts.module';
import { XrayItemType } from 'reactApp/modules/home/home.types';
import type { PublishItem } from 'reactApp/modules/modifying/modifying.types';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { loadUser } from 'reactApp/modules/user/user.thunkActions';
import {
    weblinkAccessControlLoadStart,
    weblinkAccessControlReset,
} from 'reactApp/modules/weblinkAccessControl/weblinkAccessControl.actions';
import { getACL } from 'reactApp/modules/weblinkAccessControl/weblinkAccessControl.selectors';
import { Button as ButtonComponent, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { CopyWeblink } from 'reactApp/ui/CopyWeblink/CopyWeblink';
import { Icon20ChevronLeft } from 'reactApp/ui/touchIcons';
import { getPublicUrl } from 'reactApp/utils/urlHelper';

import styles from './SharingPersonalAccessBlock.css';

interface Props {
    item: PublishItem;
    isPhone?: boolean;
    isReadOnly: boolean;
    isEmbedded: boolean;
    onBackClick: () => void;
    onCloseAccess: () => void;
    from: EFrom;
    copiedWeblinkOnMount: boolean;
}

const SharingPersonalAccessBlock = memo(
    // eslint-disable-next-line max-lines-per-function
    ({ onCloseAccess, copiedWeblinkOnMount, item, isPhone, isReadOnly, from, isEmbedded, onBackClick }: Props) => {
        const dispatch = useDispatch();

        const { isLoaded, hasError: isUserError } = useSelector(UserSelectors.getLifeCycleState);
        const isUserLoaded = isLoaded && !isUserError;
        const storage = useSelector(getCurrentStorage);
        const isAlbum = from === EFrom.ALBUMS || storage === EStorageType.albums;
        const isPublic = Boolean(item?.weblink);
        const publicUrl = (isPublic && getPublicUrl(item, isAlbum)) || '';

        const acList = useSelector(getACL);

        const xrayItemType = useMemo(() => {
            if (isAlbum) {
                return XrayItemType.album;
            } else if (item.isFolder) {
                return XrayItemType.folder;
            }
            return XrayItemType.file;
        }, [isAlbum, item.isFolder]);

        const loadAllData = useCallback(() => {
            dispatch(contactsSlice.actions.listRequest);
            dispatch(weblinkAccessControlLoadStart({ id: item?.id || '', storage: item?.storage }));
        }, [dispatch, item?.id, item?.storage]);

        useEffect(() => {
            if (!isEmbedded) {
                if (!isUserLoaded) {
                    (async () => {
                        await dispatch(loadUser);
                        await loadAllData();
                    })();
                } else {
                    loadAllData();
                }
            }

            return function cleanup() {
                if (!isEmbedded) {
                    dispatch(weblinkAccessControlReset());
                }
            };
        }, [dispatch, isEmbedded, isUserLoaded, loadAllData]);

        const onCloseAccessClick = useCallback(() => {
            const onActionClick = () => {
                sendGa('unpublish');
                if (storage === EStorageType.attaches) {
                    sendGa('attaches-unpublish');
                }

                if (!item) {
                    return;
                }

                unPublishHelper({ items: [item] });

                onCloseAccess();
            };

            renderUnpublishWeblinkDialog({ onActionClick, variant: SHARING_VARIANT.files });
        }, [storage, item, onCloseAccess]);

        return (
            <div data-qa-id="public-personal-access" className={classNames({ [styles.root_phone]: isPhone })}>
                <Button
                    data-qa-id="public-personal-access-back-button"
                    mode="link"
                    align="center"
                    size="l"
                    onClick={onBackClick}
                    before={<Icon20ChevronLeft />}
                >
                    Назад
                </Button>
                <Title level="3" className={styles.title}>
                    <span data-qa-id="public-personal-access-header">
                        Добавленные пользователи{acList.length > 0 && <span className={styles.userCounter}> {acList.length}</span>}
                    </span>
                </Title>
                <Text className={styles.description}>Настройте персональный доступ, затем отправьте пользователю ссылку нa&nbsp;файл</Text>
                <SharingNewWeblinkList
                    xrayItemType={xrayItemType}
                    item={item}
                    from={from}
                    isPhone={isPhone}
                    isReadOnly={isReadOnly}
                    variant={SHARING_VARIANT.files}
                />
                <div className={styles.copyWeblink}>
                    <CopyWeblink
                        type="sharingTabs"
                        publicUrl={publicUrl}
                        autoCopy={!copiedWeblinkOnMount && !isPhone}
                        isPhone={isPhone}
                        redesignIcon
                        // tempexp_17341-next-line
                        tooltipEnabledByExp={abSharingLinkFakedoor === 'b' && !isThemedSharedModalItem}
                    />
                    <ButtonComponent
                        qaId="sharing-close-access"
                        className={styles.btnContainer}
                        theme="base"
                        middle
                        onClick={onCloseAccessClick}
                        sizeMode={isPhone ? ButtonSizeMode.big : ButtonSizeMode.middle}
                    >
                        <div className={styles.closeAccessButton}>
                            <Icon20LockOutline />
                            <span>Закрыть доступ</span>
                        </div>
                    </ButtonComponent>
                </div>
            </div>
        );
    }
);

SharingPersonalAccessBlock.displayName = 'SharingPersonalAccessBlock';

export default SharingPersonalAccessBlock;
